import { useMemo } from 'react';

import { useIsMounted } from '@sternum/web/hooks/useIsMounted';

/**
 * Execute action inside a component after async action
 *
 * @description
 * Execute a code after any async action and make sure that it will be executed
 * only if component is still mounted
 *
 * @example
 * ```tsx
 * ///////////////////// EXAMPLE 1 *****************************
 *
 * const { execIfMounted } = useAfterAsyncAction();
 * const [isLoading, setIsLoading] = useState(false);
 *
 * const handleClick = async () => {
 *   setIsLoading(true);
 *
 *   const response = await getSomeAsyncData();
 *
 *   // After async action component can be unmounted
 *   // To avoid executing action on unmounted component we can use `execIfMounted` function
 *   execIfMounted(() => {
 *     setIsLoading(false);
 *   });
 * }
 *
 * return (
 *   <Title>Is loading - {isLoading}</Title>
 *   <Button onClick={handleClick}>Run async action</Button>
 * );
 *
 * ///////////////////// EXAMPLE 2 *****************************
 *
 * const { execIfMounted } = useAfterAsyncAction();
 * const [isLoading, setIsLoading] = useState(false);
 *
 * const handleClick = async () => {
 *   setIsLoading(true);
 *
 *   const response = await getSomeAsyncData();
 *
 *   // You can execute more async operations if component is mounted
 *   // `resp2` can be undefined if component is unmounted when `execIfMounted` function is executed
 *   const resp2 = async execIfMounted(async () => {
 *     const resp2 = await getNextAsyncData();
 *
 *     return resp2; // Every returned data will be returned by `execIfMounted` function
 *   });
 *
 *   // Now change a state of component only if component is still mounted
 *   execIfMounted(() => {
 *     setIsLoading(false);
 *   });
 *
 * }
 *
 * return (
 *   <Title>Is loading - {isLoading}</Title>
 *   <Button onClick={handleClick}>Run async action</Button>
 * );
 * ```
 *
 * @returns { execIfMounted: <ReturnType>(actionToExecuteAfterAsyncAction: () => ReturnType): ReturnType | undefined }
 */
export function useAfterAsyncAction() {
  const isMounted = useIsMounted();

  const execIfMounted = useMemo(() => {
    return function execIfMounted<ReturnType>(
      actionToExecuteAfterAsyncAction: () => ReturnType,
    ): ReturnType | undefined {
      if (!isMounted.current) {
        return undefined;
      }

      return actionToExecuteAfterAsyncAction();
    };
  }, [isMounted]);

  return { execIfMounted };
}
